import { differenceInMinutes } from 'date-fns';
import { DateTime } from 'luxon';
import { times } from 'ramda';
import { BaseError } from 'src/types/base-error';
import { ONE_MINUTE_IN_MS, SP_IN_MS, UK_TIMEZONE } from 'src/types/date';
import { Result } from 'src/types/result';
import { getErrorResult, getResult } from 'src/utils/result/utils';
import { DateInterval, TimeWindow } from 'src/utils/time-interval';

// NOTE! Assumes input is a UTC date
// clamps the input to the nearest 30 minutes past the hour (in the past), UTC, since UTC epoch
export const clampUtcDateToSameOrPrevSp = (utcDate: Date): Date =>
  new Date(
    Math.floor(+utcDate / (30 * ONE_MINUTE_IN_MS)) * (30 * ONE_MINUTE_IN_MS),
  );

// NOTE! Assumes input is a UTC date
// clamps the input to the nearest 30 minutes past the hour (in the future), UTC, since UTC epoch
export const clampUtcDateToSameOrNextSp = (utcDate: Date): Date =>
  new Date(
    Math.ceil(+utcDate / (30 * ONE_MINUTE_IN_MS)) * (30 * ONE_MINUTE_IN_MS),
  );

// NOTE! Assumes input is a UTC date
// tests that the input is either 0 or 30 minutes past the hour, UTC, since UTC epoch
export const isUtcDateAlignedToSp = (utcDate: Date): boolean =>
  +clampUtcDateToSameOrPrevSp(utcDate) === +utcDate;

// Returns the settlement period number (1-based) for the given instant,
// counted from the previous local (Europe/London) midnight. Handles BST and
// clock-change days: a day yields 48 periods normally (46 or 50 on DST
// transition days).
export const getSettlementPeriodNumber = (dateTimeUtc: Date): number => {
  const ukDateTime = DateTime.fromJSDate(dateTimeUtc, { zone: UK_TIMEZONE });
  const msSinceStartOfDay = ukDateTime
    .diff(ukDateTime.startOf('day'))
    .toMillis();

  return Math.floor(msSinceStartOfDay / SP_IN_MS) + 1;
};

export class IntervalDoesNotAlignToSpError extends BaseError {
  kind = 'IntervalDoesNotAlignToSpError' as const;

  constructor(context: { start: Date; end: Date }) {
    super(
      `Invalid record. Start and end times must be on the hour or half-hour mark`,
      { context },
    );
  }
}

export const checkIntervalsAlignToSp = <T extends DateInterval>(
  intervals: T[],
): Result<IntervalDoesNotAlignToSpError, T[]> => {
  for (const { start, end } of intervals) {
    if (!isUtcDateAlignedToSp(start) || !isUtcDateAlignedToSp(end)) {
      return getErrorResult(new IntervalDoesNotAlignToSpError({ start, end }));
    }
  }

  return getResult(intervals);
};

export const getSettlementPeriodTimeWindows = (
  timeHorizon: DateInterval,
): Result<IntervalDoesNotAlignToSpError, TimeWindow[]> => {
  const checkIntervalsAlignToSpResult = checkIntervalsAlignToSp([timeHorizon]);
  if (!checkIntervalsAlignToSpResult.success) {
    return checkIntervalsAlignToSpResult.asLeftOf();
  }

  const numberOfSPs =
    differenceInMinutes(timeHorizon.end, timeHorizon.start) / 30;

  const intervals: TimeWindow[] = times((i) => {
    const start = +timeHorizon.start + i * SP_IN_MS;
    const end = start + SP_IN_MS;

    return [start, end];
  }, numberOfSPs);

  return getResult(intervals);
};
